import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const FilterBar = ({ onFilterApply }) => {
    const defaultFilters = {
        dateRange: { type: 'all', from: '', to: '' },
        location: 'All',
        globalEvent: 'All',
        estimatedLoss: 'All'
    };

    const [filters, setFilters] = useState(defaultFilters);
    const [lastAppliedFilters, setLastAppliedFilters] = useState(defaultFilters);

    // Temporary state to hold changes before "Apply" is clicked, if we want immediate feedback or batched.
    // The prompt says "I need to add a button for applying", implying batched updates.
    // So distinct state for UI selection vs valid filters?
    // Usually pure React state is fine, we just trigger the "Apply" logic (e.g. API call) on button click.
    // For now, I'll just update the state directly.

    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleDropdown = (name) => {
        setOpenDropdown(openDropdown === name ? null : name);
    };

    const handleApply = () => {
        // Format filters for API
        const apiFilters = {
            date_from: filters.dateRange.type === 'custom' && filters.dateRange.from ? filters.dateRange.from : '',
            date_to: filters.dateRange.type === 'custom' && filters.dateRange.to ? filters.dateRange.to : '',
            location: filters.location,
            global_event: filters.globalEvent,
            estimated_loss_range: filters.estimatedLoss
        };

        console.log("Filters Applied:", apiFilters);

        // Call parent callback to trigger data refresh
        if (onFilterApply) {
            onFilterApply(apiFilters);
        }

        setLastAppliedFilters(filters);
        setOpenDropdown(null);
    };

    const handleReset = () => {
        setFilters(defaultFilters);

        // Immediately apply default filters
        const apiFilters = {
            date_from: '',
            date_to: '',
            location: defaultFilters.location,
            global_event: defaultFilters.globalEvent,
            estimated_loss_range: defaultFilters.estimatedLoss
        };

        console.log("Filters Reset:", apiFilters);
        if (onFilterApply) {
            onFilterApply(apiFilters);
        }

        setLastAppliedFilters(defaultFilters);
        setOpenDropdown(null);
    };

    const countries = [
        'United States', 'United Kingdom', 'Canada', 'Germany',
        'France', 'Australia', 'Japan', 'Brazil', 'India', 'China'
    ];

    const estimatedLossOptions = [
        '<$50K', '$50K–$250K', '$250K–$1M', '>$1M'
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2" ref={dropdownRef}>
            {/* Date of Loss Filter */}
            <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-500 mb-1 ml-1 uppercase tracking-wide">Date of Loss</label>
                <div className="relative">
                    <button
                        onClick={() => toggleDropdown('date')}
                        className={`nav-filter-btn w-full bg-[#F3F5F7] hover:bg-gray-200 transition-colors text-gray-700 px-3 py-2 rounded-lg flex justify-between items-center font-medium ${openDropdown === 'date' ? 'ring-2 ring-[#006BB3]' : ''}`}
                    >
                        <span className="truncate">
                            {filters.dateRange.type === 'custom' && filters.dateRange.from
                                ? `${filters.dateRange.from} ${filters.dateRange.to ? '- ' + filters.dateRange.to : ''}`
                                : 'All'}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'date' ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === 'date' && (
                        <div className="absolute top-full left-0 mt-2 w-full md:w-72 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
                            <div className="space-y-3">
                                <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded-lg"
                                    onClick={() => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, type: 'all' } }))}
                                >
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${filters.dateRange.type === 'all' ? 'border-[#006BB3] bg-[#006BB3]' : 'border-gray-300'}`}>
                                        {filters.dateRange.type === 'all' && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <span className="text-gray-700 font-medium">All Dates</span>
                                </label>

                                <div className="border-t border-gray-100 my-2 pt-2">
                                    <p className="text-sm text-gray-500 mb-2 font-medium px-2">Custom Range</p>
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-xs text-gray-400 ml-1">From</label>
                                            <input
                                                type="date"
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#006BB3]"
                                                value={filters.dateRange.from}
                                                onChange={(e) => {
                                                    const newFrom = e.target.value;
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        dateRange: { type: 'custom', from: newFrom, to: prev.dateRange.to }
                                                    }));
                                                    if (newFrom && filters.dateRange.to) {
                                                        setOpenDropdown(null);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-400 ml-1">To</label>
                                            <input
                                                type="date"
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#006BB3]"
                                                value={filters.dateRange.to}
                                                onChange={(e) => {
                                                    const newTo = e.target.value;
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        dateRange: { type: 'custom', from: prev.dateRange.from, to: newTo }
                                                    }));
                                                    if (filters.dateRange.from && newTo) {
                                                        setOpenDropdown(null);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Loss Locations Filter */}
            <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-500 mb-1 ml-1 uppercase tracking-wide">Loss Location</label>
                <div className="relative">
                    <button
                        onClick={() => toggleDropdown('location')}
                        className={`nav-filter-btn w-full bg-[#F3F5F7] hover:bg-gray-200 transition-colors text-gray-700 px-3 py-2 rounded-lg flex justify-between items-center font-medium ${openDropdown === 'location' ? 'ring-2 ring-[#006BB3]' : ''}`}
                    >
                        <span className="truncate">
                            {filters.location}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'location' ? 'rotate-180' : ''}`} />
                    </button>

                    {openDropdown === 'location' && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50 max-h-64 overflow-y-auto">
                            <div
                                onClick={() => {
                                    setFilters(prev => ({ ...prev, location: 'All' }));
                                    setOpenDropdown(null);
                                }}
                                className={`px-3 py-2 rounded-lg cursor-pointer flex justify-between items-center ${filters.location === 'All' ? 'bg-[#006BB3]/10 text-[#006BB3]' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                                <span>All</span>
                                {filters.location === 'All' && <Check className="w-4 h-4" />}
                            </div>
                            {countries.map(country => (
                                <div
                                    key={country}
                                    onClick={() => {
                                        setFilters(prev => ({ ...prev, location: country }));
                                        setOpenDropdown(null);
                                    }}
                                    className={`px-3 py-2 rounded-lg cursor-pointer flex justify-between items-center ${filters.location === country ? 'bg-[#006BB3]/10 text-[#006BB3]' : 'hover:bg-gray-50 text-gray-700'}`}
                                >
                                    <span>{country}</span>
                                    {filters.location === country && <Check className="w-4 h-4" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Global Loss Event Filter */}
            <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-500 mb-1 ml-1 uppercase tracking-wide">Global Loss Event</label>
                <div className="relative">
                    <button
                        onClick={() => toggleDropdown('globalEvent')}
                        className={`nav-filter-btn w-full bg-[#F3F5F7] hover:bg-gray-200 transition-colors text-gray-700 px-3 py-2 rounded-lg flex justify-between items-center font-medium ${openDropdown === 'globalEvent' ? 'ring-2 ring-[#006BB3]' : ''}`}
                    >
                        <span className="truncate">
                            {filters.globalEvent}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'globalEvent' ? 'rotate-180' : ''}`} />
                    </button>

                    {openDropdown === 'globalEvent' && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
                            {['All', 'On', 'Off'].map(option => (
                                <div
                                    key={option}
                                    onClick={() => {
                                        setFilters(prev => ({ ...prev, globalEvent: option }));
                                        setOpenDropdown(null);
                                    }}
                                    className={`px-3 py-2 rounded-lg cursor-pointer flex justify-between items-center ${filters.globalEvent === option ? 'bg-[#006BB3]/10 text-[#006BB3]' : 'hover:bg-gray-50 text-gray-700'}`}
                                >
                                    <span>{option}</span>
                                    {filters.globalEvent === option && <Check className="w-4 h-4" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Estimated Loss Filter */}
            <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-500 mb-1 ml-1 uppercase tracking-wide">Estimated Loss</label>
                <div className="relative">
                    <button
                        onClick={() => toggleDropdown('estimatedLoss')}
                        className={`nav-filter-btn w-full bg-[#F3F5F7] hover:bg-gray-200 transition-colors text-gray-700 px-3 py-2 rounded-lg flex justify-between items-center font-medium ${openDropdown === 'estimatedLoss' ? 'ring-2 ring-[#006BB3]' : ''}`}
                    >
                        <span className="truncate">
                            {filters.estimatedLoss}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'estimatedLoss' ? 'rotate-180' : ''}`} />
                    </button>

                    {openDropdown === 'estimatedLoss' && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
                            <div
                                onClick={() => {
                                    setFilters(prev => ({ ...prev, estimatedLoss: 'All' }));
                                    setOpenDropdown(null);
                                }}
                                className={`px-3 py-2 rounded-lg cursor-pointer flex justify-between items-center ${filters.estimatedLoss === 'All' ? 'bg-[#006BB3]/10 text-[#006BB3]' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                                <span>All</span>
                                {filters.estimatedLoss === 'All' && <Check className="w-4 h-4" />}
                            </div>
                            {estimatedLossOptions.map(option => (
                                <div
                                    key={option}
                                    onClick={() => {
                                        setFilters(prev => ({ ...prev, estimatedLoss: option }));
                                        setOpenDropdown(null);
                                    }}
                                    className={`px-3 py-2 rounded-lg cursor-pointer flex justify-between items-center ${filters.estimatedLoss === option ? 'bg-[#006BB3]/10 text-[#006BB3]' : 'hover:bg-gray-50 text-gray-700'}`}
                                >
                                    <span>{option}</span>
                                    {filters.estimatedLoss === option && <Check className="w-4 h-4" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Button */}
            <div className="flex flex-col justify-end">
                <button
                    onClick={JSON.stringify(filters) !== JSON.stringify(lastAppliedFilters) || JSON.stringify(filters) === JSON.stringify(defaultFilters) ? handleApply : handleReset}
                    className={`w-full transition-all duration-200 px-4 py-2 rounded-lg font-semibold shadow-md flex justify-center items-center ${JSON.stringify(filters) !== JSON.stringify(lastAppliedFilters) || JSON.stringify(filters) === JSON.stringify(defaultFilters)
                        ? 'bg-[#006BB3] hover:bg-[#005a99] text-white'
                        : 'bg-white text-[#006BB3] ring-2 ring-inset ring-[#006BB3] hover:!bg-[#006BB3] hover:!text-white'
                        }`}
                >
                    {JSON.stringify(filters) !== JSON.stringify(lastAppliedFilters) || JSON.stringify(filters) === JSON.stringify(defaultFilters) ? 'Apply' : 'Reset All'}
                </button>
            </div>
        </div>
    );
};

export default FilterBar;
